import * as React from "react"
import { Control, Emitter } from "rete"
import { debounce } from "lodash"
import { EventsTypes } from "rete/types/events"
import { GeneratePieChart } from "../../analysis_panel_tools/charts/pie"
import { GenerateBarChart } from "../../analysis_panel_tools/charts/bar"
import { GenerateHistogram } from "../../analysis_panel_tools/charts/histogram"
import type { ChartData } from "../../analysis_panel_tools/subsection"
import { formatTileGridUnitLabel, type TileGridProps } from "../tile_grid"
import { TableRowMeanBars, type TableRowMeanSort } from "../../analysis_panel_tools/charts/table_row_mean_bars"
import { SPIDER_SERIES_COLORS, TableRowMeanSpider } from "../../analysis_panel_tools/charts/table_row_mean_spider"
import {
  MAX_HISTOGRAM_BINS,
  readHistogramBins,
  spiderChartHasData,
  tableCompareDifference,
  tableCompareEndpointNames,
  type DatasetChartKind,
  type SerializedDatasetChart,
  type SerializedSpiderChart,
  type SerializedTableChart,
  type SerializedTableCompareChart,
} from "../dataset_chart_data"
import "../../snapshot/analysis_panel.css"

type DatasetChartNodeProps = {
  kind: DatasetChartKind
  chart: SerializedDatasetChart | null
  getBins: () => number
  setBins: (bins: number) => void
}

const NODE_PANEL_WIDTH = 320
const EXPANDED_PANEL_WIDTH = 760
const EXPANDED_PANEL_HEIGHT = 560
let chartSvgSeq = 0

function chartDataFromSerialized(chart: SerializedDatasetChart): ChartData {
  return {
    count: new Map(chart.count),
    colors: new Map(chart.colors),
    numeric_stats: chart.numericStats,
    full_numeric_stats: chart.fullNumericStats ?? chart.numericStats,
    inputColors: undefined,
    inputFillType: chart.inputFillType,
    inputHistogramBins: chart.inputHistogramBins,
  }
}

function snapshotChartType(kind: DatasetChartKind): "pie" | "bar" | "hist" {
  if (kind === "distribution") return "bar"
  return kind
}

function withoutNoData(chartData: ChartData): ChartData {
  const isNoData = (key: unknown) => typeof key === "string" && key.trim().toLowerCase() === "no data"
  return {
    ...chartData,
    count: new Map(Array.from(chartData.count.entries()).filter(([key]) => !isNoData(key))),
    colors: new Map(Array.from(chartData.colors.entries()).filter(([key]) => !isNoData(key))),
  }
}

export function DatasetSnapshotChart({
  chart,
  kind,
  expanded = false,
  showBinEditor = false,
  bins,
  onBinsChange,
}: {
  chart: SerializedDatasetChart
  kind: DatasetChartKind
  expanded?: boolean
  showBinEditor?: boolean
  bins?: number
  onBinsChange?: (bins: number) => void
}) {
  const [hideNoDataValues, setHideNoDataValues] = React.useState(false)
  const [showStdDev, setShowStdDev] = React.useState(false)
  const [decimalPlaces, setDecimalPlaces] = React.useState(2)
  const [svgId] = React.useState(() => `dataset-${kind}-${++chartSvgSeq}`)
  const chartData = React.useMemo(() => chartDataFromSerialized(chart), [chart])
  const visibleChartData = React.useMemo(
    () => (hideNoDataValues ? withoutNoData(chartData) : chartData),
    [chartData, hideNoDataValues]
  )
  const chartType = snapshotChartType(kind)
  const categorical = chart.sourceType !== "NumericTileGrid"
  const compact = !expanded
  const panelWidth = expanded ? EXPANDED_PANEL_WIDTH : NODE_PANEL_WIDTH

  return (
    <div className="bg-white text-dark" style={{ width: expanded ? "100%" : NODE_PANEL_WIDTH, maxWidth: "100%" }}>
      {categorical && (
        <div className="d-flex justify-content-end px-2 pt-2">
          <button
            type="button"
            title="Filter missing data"
            className={`btn btn-sm btn-${hideNoDataValues ? "primary" : "outline-primary"}`}
            onClick={() => setHideNoDataValues(value => !value)}
            onPointerDown={event => event.stopPropagation()}
          >
            <i className="fas fa-filter" />
          </button>
        </div>
      )}
      {chartType === "pie" && (
        <GeneratePieChart
          chartData={visibleChartData}
          compact={compact}
          panelWidth={panelWidth}
          panelHeight={expanded ? EXPANDED_PANEL_HEIGHT : undefined}
          svgId={svgId}
        />
      )}
      {chartType === "bar" && (
        <GenerateBarChart
          chartData={visibleChartData}
          compact={compact}
          panelWidth={panelWidth}
          panelHeight={expanded ? EXPANDED_PANEL_HEIGHT : undefined}
          svgId={svgId}
        />
      )}
      {chartType === "hist" && (
        <GenerateHistogram
          chartData={visibleChartData}
          props={chart.props}
          cellArea={chart.cellArea}
          compact={compact}
          panelWidth={panelWidth}
          panelHeight={expanded ? EXPANDED_PANEL_HEIGHT : undefined}
          svgId={svgId}
        />
      )}
      <DatasetChartLegend
        chart={chart}
        chartData={chartData}
        hideNoDataValues={hideNoDataValues}
        showStdDev={showStdDev}
        setShowStdDev={setShowStdDev}
        decimalPlaces={decimalPlaces}
        compact={compact}
      />
      {(showBinEditor || chart.sourceType === "NumericTileGrid") && (
        <div className="d-flex align-items-center flex-wrap px-2 pb-2" style={{ gap: 8 }}>
          {showBinEditor && onBinsChange && (
            <label className="mb-0 d-flex align-items-center" style={{ gap: 6, fontSize: "0.8rem" }}>
              Bars
              <input
                type="number"
                min={1}
                max={MAX_HISTOGRAM_BINS}
                value={bins ?? chart.inputHistogramBins}
                style={{ width: 56 }}
                onChange={event => {
                  const next = readHistogramBins(event.target.value)
                  if (event.target.value !== "" && Number.isFinite(Number(event.target.value))) {
                    onBinsChange(next)
                  }
                }}
                onPointerDown={event => event.stopPropagation()}
              />
            </label>
          )}
          {chart.sourceType === "NumericTileGrid" && (
            <label className="mb-0 d-flex align-items-center" style={{ gap: 6, fontSize: "0.8rem" }}>
              Decimal places
              <input
                type="number"
                min={0}
                max={8}
                value={decimalPlaces}
                style={{ width: 52 }}
                onChange={event => {
                  const next = Number(event.target.value)
                  if (Number.isFinite(next)) setDecimalPlaces(Math.max(0, Math.min(8, Math.round(next))))
                }}
                onPointerDown={event => event.stopPropagation()}
              />
            </label>
          )}
        </div>
      )}
    </div>
  )
}

function DatasetChartLegend({
  chart,
  chartData,
  hideNoDataValues,
  showStdDev,
  setShowStdDev,
  decimalPlaces,
  compact,
}: {
  chart: SerializedDatasetChart
  chartData: ChartData
  hideNoDataValues: boolean
  showStdDev: boolean
  setShowStdDev: (show: boolean) => void
  decimalPlaces: number
  compact: boolean
}) {
  if (chart.sourceType === "NumericTileGrid") {
    const stats = chartData.full_numeric_stats
    if (!stats) return null
    return (
      <div className={`chart-legend${compact ? " chart-legend--compact" : ""}`}>
        <div className="d-flex justify-content-end mb-1">
          <button
            type="button"
            className={`btn btn-sm ${showStdDev ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setShowStdDev(!showStdDev)}
            onPointerDown={event => event.stopPropagation()}
          >
            {showStdDev ? "Stats" : "Std dev"}
          </button>
        </div>
        {!showStdDev ? (
          <div className="chart-legend-numeric-stats">
            <StatRow label="Sum" value={stats.sum} decimalPlaces={decimalPlaces} props={chart.props} perArea={false} variant="sum" />
            <div className="chart-legend-numeric-stats-grid">
              <StatRow label="Min" value={stats.min} decimalPlaces={decimalPlaces} props={chart.props} perArea />
              <StatRow label="Max" value={stats.max} decimalPlaces={decimalPlaces} props={chart.props} perArea />
              <StatRow label="Median" value={stats.median} decimalPlaces={decimalPlaces} props={chart.props} perArea />
              <StatRow label="Range" value={stats.range} decimalPlaces={decimalPlaces} props={chart.props} perArea />
              <StatRow label="Mean" value={stats.mean} decimalPlaces={decimalPlaces} props={chart.props} perArea />
              <StatRow label="Mode" value={stats.mode} decimalPlaces={decimalPlaces} props={chart.props} perArea />
            </div>
          </div>
        ) : (
          <div className="chart-legend-numeric-stats">
            <StatRow label="Sum" value={stats.stdDevSum} decimalPlaces={decimalPlaces} props={chart.props} perArea={false} variant="sum" />
            <div className="chart-legend-numeric-stats-grid">
              <StatRow label="Mean" value={stats.stdDevMean} decimalPlaces={decimalPlaces} props={chart.props} perArea />
            </div>
          </div>
        )}
      </div>
    )
  }

  const entries = Array.from(chartData.count.entries()).sort((a, b) => b[1] - a[1])
  const total = entries.reduce((sum, [, count]) => sum + count, 0)
  const noData = entries.reduce((sum, [key, count]) => {
    return sum + (String(key).trim().toLowerCase() === "no data" ? count : 0)
  }, 0)
  const factor = hideNoDataValues && total - noData > 0 ? total / (total - noData) : 1

  return (
    <div className={`chart-legend${compact ? " chart-legend--compact" : ""}`}>
      <div className="chart-legend-note">
        {hideNoDataValues
          ? "Missing data points removed from distribution"
          : "Missing data points included in distribution; labelled as \"No data\""}
      </div>
      <div className="chart-legend-badges">
        {entries.map(([label, count]) => {
          const labelStr = String(label)
          const isNoData = labelStr.trim().toLowerCase() === "no data"
          const basePct = total > 0 ? (count / total) * 100 : 0
          const adjPct = basePct * factor
          return (
            <span
              key={labelStr}
              className={`chart-legend-badge${isNoData && hideNoDataValues ? " chart-legend-badge--strike" : ""}`}
              title={labelStr}
            >
              <span
                className="chart-legend-badge__swatch"
                style={{ backgroundColor: `rgb(${chartData.colors.get(label)?.slice(0, 3)?.join(",") ?? "158,158,158"})` }}
              />
              <span className="chart-legend-badge__label">{labelStr}</span>
              <span className={`chart-legend-badge__pct${hideNoDataValues && !isNoData ? " chart-legend-badge__pct--struck" : ""}`}>
                {basePct.toFixed(1)}%
              </span>
              {hideNoDataValues && !isNoData && (
                <span className="chart-legend-badge__pct chart-legend-badge__pct--adj">
                  {adjPct.toFixed(1)}%
                </span>
              )}
            </span>
          )
        })}
      </div>
    </div>
  )
}

function StatRow({
  label,
  value,
  decimalPlaces,
  props,
  perArea,
  variant = "grid",
}: {
  label: string
  value: number
  decimalPlaces: number
  props: TileGridProps | undefined
  perArea: boolean
  variant?: "sum" | "grid"
}) {
  const unit = formatTileGridUnitLabel(props, { perArea })
  return (
    <div className={`chart-legend-numeric-stat-row${variant === "sum" ? " chart-legend-numeric-stat-row--sum" : ""}`}>
      <label>{label}</label>
      <input
        disabled
        type="text"
        className="chart-legend-numeric-stat-value"
        value={parseFloat(value.toFixed(decimalPlaces)).toLocaleString()}
      />
      <span className="chart-legend-numeric-stat-unit" title={unit}>{unit}</span>
    </div>
  )
}

function DatasetChartNodeView({ kind, chart, getBins, setBins }: DatasetChartNodeProps) {
  const [, forceUpdate] = React.useReducer(count => count + 1, 0)

  if (!chart || chart.count.length === 0) {
    return (
      <div className="bg-white text-muted p-3" style={{ width: NODE_PANEL_WIDTH, maxWidth: "100%", fontSize: "0.85rem" }}>
        {chart ? "This dataset has no values to chart." : "Connect a dataset to draw this chart."}
      </div>
    )
  }

  return (
    <DatasetSnapshotChart
      chart={chart}
      kind={kind}
      showBinEditor={kind === "hist"}
      bins={getBins()}
      onBinsChange={bins => {
        setBins(bins)
        forceUpdate()
      }}
    />
  )
}

const TABLE_NODE_WIDTH = 420

export function TableSnapshotChart({
  chart,
  expanded = false,
}: {
  chart: SerializedTableChart
  expanded?: boolean
}) {
  const [sortType, setSortType] = React.useState<TableRowMeanSort>("tableOrder")
  const [svgId] = React.useState(() => `dataset-table-${++chartSvgSeq}`)
  const compact = !expanded
  const panelWidth = expanded ? EXPANDED_PANEL_WIDTH : TABLE_NODE_WIDTH
  const animationResetKey = chart.rows.map(row => `${row.label}:${row.mean}`).join("|")

  return (
    <div
      className="bg-white text-dark table-mean-chart"
      style={{ width: expanded ? "100%" : TABLE_NODE_WIDTH, maxWidth: "100%" }}
    >
      <div className="table-mean-chart__diff-title">
        <span className="table-mean-chart__diff-order-label" title={chart.tableName}>
          <span className="table-mean-chart__diff-order-name">{chart.tableName}</span>
        </span>
      </div>
      <div className="table-mean-chart__controls-row">
        <div className="table-mean-chart__control-field">
          <label className="table-mean-chart__control-label" htmlFor={`${svgId}-sort`}>
            Sort
          </label>
          <select
            id={`${svgId}-sort`}
            className="table-mean-chart__select"
            value={sortType}
            onChange={event => setSortType(event.target.value as TableRowMeanSort)}
            onPointerDown={event => event.stopPropagation()}
          >
            <option value="valueDesc">Highest first</option>
            <option value="valueAsc">Lowest first</option>
            <option value="tableOrder">Table order</option>
          </select>
        </div>
      </div>
      <TableRowMeanBars
        rows={chart.rows}
        axisLabel="Area-weighted mean"
        svgId={svgId}
        compact={compact}
        panelWidth={panelWidth}
        animationResetKey={animationResetKey}
        sortType={sortType}
      />
    </div>
  )
}

function TableChartNodeView({ chart }: { chart: SerializedTableChart | null }) {
  if (!chart || chart.rows.length === 0) {
    return (
      <div className="bg-white text-muted p-3" style={{ width: TABLE_NODE_WIDTH, maxWidth: "100%", fontSize: "0.85rem" }}>
        {chart
          ? "Table columns do not match the category labels, or the matching cells are empty."
          : "Connect a categorical dataset and a table."}
      </div>
    )
  }
  return <TableSnapshotChart chart={chart} />
}

export function TableCompareSnapshotChart({
  chart,
  expanded = false,
}: {
  chart: SerializedTableCompareChart
  expanded?: boolean
}) {
  const [sortType, setSortType] = React.useState<TableRowMeanSort>("tableOrder")
  const [swapped, setSwapped] = React.useState(false)
  const [svgId] = React.useState(() => `dataset-table-compare-${++chartSvgSeq}`)
  const compact = !expanded
  const panelWidth = expanded ? EXPANDED_PANEL_WIDTH : TABLE_NODE_WIDTH
  const names = tableCompareEndpointNames(chart, swapped)
  const rows = tableCompareDifference(chart, swapped)
  const animationResetKey = `${names.from}->${names.to}:${rows.map(row => `${row.label}:${row.mean}`).join("|")}`

  return (
    <div
      className={`bg-white text-dark table-mean-chart${compact ? " table-mean-chart--compact" : ""}`}
      style={{ width: expanded ? "100%" : TABLE_NODE_WIDTH, maxWidth: "100%" }}
    >
      <div className="table-mean-chart__diff-title">
        <span className="table-mean-chart__diff-order-label" title={`${names.from} → ${names.to}`}>
          <span className="table-mean-chart__diff-order-name">{names.from}</span>
          <span className="table-mean-chart__diff-order-op" aria-hidden="true">→</span>
          <span className="table-mean-chart__diff-order-name">{names.to}</span>
        </span>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm table-mean-chart__diff-swap"
          aria-label="Swap comparison order"
          title="Swap comparison order"
          onClick={() => setSwapped(value => !value)}
          onPointerDown={event => event.stopPropagation()}
        >
          <i className="fas fa-exchange-alt" aria-hidden="true" />
        </button>
      </div>
      <div className="table-mean-chart__controls-row">
        <div className="table-mean-chart__control-field">
          <label className="table-mean-chart__control-label" htmlFor={`${svgId}-sort`}>
            Sort
          </label>
          <select
            id={`${svgId}-sort`}
            className="table-mean-chart__select"
            value={sortType}
            onChange={event => setSortType(event.target.value as TableRowMeanSort)}
            onPointerDown={event => event.stopPropagation()}
          >
            <option value="valueDesc">Highest first</option>
            <option value="valueAsc">Lowest first</option>
            <option value="tableOrder">Table order</option>
          </select>
        </div>
      </div>
      <TableRowMeanBars
        rows={rows}
        axisLabel="Change in area-weighted mean"
        svgId={svgId}
        compact={compact}
        panelWidth={panelWidth}
        animationResetKey={animationResetKey}
        sortType={sortType}
      />
    </div>
  )
}

function tableCompareEmptyMessage(chart: SerializedTableCompareChart | null): string {
  if (!chart) return "Connect categorical datasets A and B, and a table."
  if (chart.rowsA.length === 0 || chart.rowsB.length === 0) {
    return "Table columns do not match the category labels, or the matching cells are empty."
  }
  return "A and B have no table rows in common."
}

function TableCompareChartNodeView({ chart }: { chart: SerializedTableCompareChart | null }) {
  if (!chart || tableCompareDifference(chart, false).length === 0) {
    return (
      <div className="bg-white text-muted p-3" style={{ width: TABLE_NODE_WIDTH, maxWidth: "100%", fontSize: "0.85rem" }}>
        {tableCompareEmptyMessage(chart)}
      </div>
    )
  }
  return <TableCompareSnapshotChart chart={chart} />
}

export class TableCompareChartControl extends Control {
  props: { chart: SerializedTableCompareChart | null }
  component: (props: { chart: SerializedTableCompareChart | null }) => JSX.Element

  constructor(key: string) {
    super(key)
    this.props = { chart: null }
    this.component = TableCompareChartNodeView
  }

  setChart(chart: SerializedTableCompareChart | null) {
    this.props.chart = chart
  }
}

const SPIDER_NODE_WIDTH = 480

function expandedSpiderLayoutSize(windowHeight: number, contained = false): number {
  const modalHeight = contained
    ? Math.min(windowHeight * 0.74, 760)
    : Math.min(windowHeight * 0.88, 900)
  const outsideChart = contained ? 200 : 180
  const available = modalHeight - outsideChart
  const target = windowHeight * (contained ? 0.58 : 0.72)
  return Math.round(Math.max(240, Math.min(target, available)))
}

function useWindowHeight(): number {
  const [height, setHeight] = React.useState(() => (typeof window === "undefined" ? 800 : window.innerHeight))
  React.useEffect(() => {
    const onResize = () => setHeight(window.innerHeight)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])
  return height
}

export function SpiderSnapshotChart({
  chart,
  expanded = false,
  contained = false,
}: {
  chart: SerializedSpiderChart
  expanded?: boolean
  contained?: boolean
}) {
  const [svgId] = React.useState(() => `dataset-spider-${++chartSvgSeq}`)
  const [hiddenNames, setHiddenNames] = React.useState<string[]>([])
  const compact = !expanded
  const windowHeight = useWindowHeight()
  const layoutSize = expanded ? expandedSpiderLayoutSize(windowHeight, contained) : undefined
  const plotted = chart.series.filter(entry => entry.rows.length > 0)
  const visible = plotted.filter(entry => !hiddenNames.includes(entry.name))
  const primaryRows = chart.axes.map(label => ({ label, mean: 0 }))
  const series = visible.map(entry => {
    const colorIndex = plotted.findIndex(item => item.name === entry.name)
    const color = SPIDER_SERIES_COLORS[(colorIndex >= 0 ? colorIndex : 0) % SPIDER_SERIES_COLORS.length]
    return { id: entry.name, name: entry.name, rows: entry.rows, color }
  })
  const animationResetKey = series.map(entry => `${entry.name}:${entry.rows.map(row => `${row.label}:${row.mean}`).join(",")}`).join("|")

  const toggleSeries = (name: string) => {
    setHiddenNames(current => current.includes(name) ? current.filter(item => item !== name) : [...current, name])
  }

  return (
    <div
      className={`bg-white text-dark table-mean-chart${compact ? " table-mean-chart--compact" : ""}`}
      style={{ width: expanded ? "100%" : SPIDER_NODE_WIDTH, maxWidth: "100%" }}
    >
      <div className="table-mean-chart__diff-title">
        <span className="table-mean-chart__diff-order-label" title={chart.tableName}>
          <span className="table-mean-chart__diff-order-name">{chart.tableName}</span>
        </span>
      </div>
      <TableRowMeanSpider
        series={series}
        primaryRows={primaryRows}
        axisLabel="Area-weighted mean"
        svgId={svgId}
        compact={compact}
        panelWidth={compact ? SPIDER_NODE_WIDTH : layoutSize}
        layoutSize={layoutSize}
        animationResetKey={animationResetKey}
      />
      {plotted.length > 0 && (
        <div className="table-mean-chart__spider-legend" role="group" aria-label="Datasets">
          {plotted.map((entry, index) => {
            const color = SPIDER_SERIES_COLORS[index % SPIDER_SERIES_COLORS.length]
            const inputId = `${svgId}-legend-${index}`
            return (
              <label key={entry.name} className="table-mean-chart__spider-legend-item" htmlFor={inputId}>
                <input
                  id={inputId}
                  type="checkbox"
                  checked={!hiddenNames.includes(entry.name)}
                  onChange={() => toggleSeries(entry.name)}
                  onPointerDown={event => event.stopPropagation()}
                />
                <span className="table-mean-chart__spider-legend-swatch" style={{ backgroundColor: color.stroke }} />
                <span title={entry.name}>{entry.name}</span>
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}

function SpiderChartNodeView({ chart }: { chart: SerializedSpiderChart | null }) {
  if (!chart || !spiderChartHasData(chart)) {
    return (
      <div className="bg-white text-muted p-3" style={{ width: SPIDER_NODE_WIDTH, maxWidth: "100%", fontSize: "0.85rem" }}>
        {chart
          ? "Table columns do not match the category labels, or the matching cells are empty."
          : "Connect categorical datasets and a table."}
      </div>
    )
  }
  return <SpiderSnapshotChart chart={chart} />
}

export class SpiderChartControl extends Control {
  props: { chart: SerializedSpiderChart | null }
  component: (props: { chart: SerializedSpiderChart | null }) => JSX.Element

  constructor(key: string) {
    super(key)
    this.props = { chart: null }
    this.component = SpiderChartNodeView
  }

  setChart(chart: SerializedSpiderChart | null) {
    this.props.chart = chart
  }
}

export class TableChartControl extends Control {
  props: { chart: SerializedTableChart | null }
  component: (props: { chart: SerializedTableChart | null }) => JSX.Element

  constructor(key: string) {
    super(key)
    this.props = { chart: null }
    this.component = TableChartNodeView
  }

  setChart(chart: SerializedTableChart | null) {
    this.props.chart = chart
  }
}

export class DatasetChartControl extends Control {
  props: DatasetChartNodeProps
  component: (props: DatasetChartNodeProps) => JSX.Element

  constructor(emitter: Emitter<EventsTypes> | null, key: string, kind: DatasetChartKind) {
    super(key)
    const notify = debounce(() => emitter?.trigger("process"), 400)
    this.props = {
      kind,
      chart: null,
      getBins: () => readHistogramBins(this.getData("histogramBins")),
      setBins: bins => {
        this.putData("histogramBins", bins)
        notify()
      },
    }
    this.component = DatasetChartNodeView
  }

  setChart(chart: SerializedDatasetChart | null) {
    this.props.chart = chart
  }
}
